import React, { useState, ReactNode } from 'react';
import { Stack } from '@mui/material';
import { CustomAccordion } from './CustomAccordion';

export interface AccordionItem {
  id: string;
  title: string;
  content: ReactNode;
  defaultExpanded?: boolean;
  disabled?: boolean;
  elevation?: number;
  sx?: Record<string, any>;
}

export interface AccordionGroupProps {
  items: AccordionItem[];
  allowMultiple?: boolean;
  spacing?: number;
  elevation?: number;
  sx?: Record<string, any>;
  onItemToggle?: (itemId: string, isExpanded: boolean) => void;
}

export const AccordionGroup: React.FC<AccordionGroupProps> = ({
  items,
  allowMultiple = false,
  spacing = 2,
  elevation = 0,
  sx = {},
  onItemToggle,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(
    new Set(items.filter(item => item.defaultExpanded).map(item => item.id))
  );

  const handleToggle = (itemId: string, isExpanded: boolean) => {
    if (allowMultiple) {
      setExpandedItems(prev => {
        const newSet = new Set(prev);
        if (isExpanded) {
          newSet.add(itemId);
        } else {
          newSet.delete(itemId);
        }
        return newSet;
      });
    } else {
      setExpandedItems(isExpanded ? new Set([itemId]) : new Set());
    }

    onItemToggle?.(itemId, isExpanded);
  };

  return (
    <Stack spacing={spacing} sx={sx}>
      {items.map(item => (
        <CustomAccordion
          key={item.id}
          title={item.title}
          expanded={expandedItems.has(item.id)}
          onChange={(_, isExpanded) => handleToggle(item.id, isExpanded)}
          elevation={item.elevation ?? elevation}
          sx={item.sx}
        >
          {item.content}
        </CustomAccordion>
      ))}
    </Stack>
  );
};

export default AccordionGroup;
